import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  SortingState,
  ColumnDef as TanStackColumnDef,
  useReactTable,
} from "@tanstack/react-table";
import React, { Fragment, useEffect } from "react";
import { Button, Col, Row, Table } from "react-bootstrap";
import DateRangeFilterPopover from "./DateRangeFilterPopover";
import FilterPopover from "./FilterPopover";
import Header from "Layout/Header";
import Pagination from "Components/Pagination/Pagination";

type FilterType = "dropdown" | "text" | "date" | "boolean";

export type CustomColumnDef<TData> = TanStackColumnDef<TData> & {
  filterType?: FilterType;
  filterOptions?: string[];
  filterFetchUrl?: string;
};

interface TableContainerProps<TData = any> {
  columns: CustomColumnDef<TData>[];
  data: TData[];
  totalRecords: number;
  pageIndex: number;
  pageSize: number;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  columnFilters?: ColumnFiltersState;
  setColumnFilters?: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  colValue: any;
  isBordered?: boolean;
  isPagination?: boolean;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  manualSorting: boolean;
  isGlobalFilter?: boolean;
  SearchPlaceholder?: string;
  scrollableFilterPopover?: boolean;
}

const TableContainer = ({
  columns,
  data,
  totalRecords,
  pageIndex,
  pageSize,
  onPageChange,
  onPageSizeChange,
  columnFilters,
  setColumnFilters,
  sorting,
  onSortingChange,
  manualSorting = true,
  isBordered = false,
  isPagination = true,
  colValue,
  scrollableFilterPopover = false,
}: TableContainerProps) => {
  const handleColumnFilterChange: OnChangeFn<ColumnFiltersState> = (
    updaterOrValue
  ) => {
    if (!setColumnFilters) return;
    setColumnFilters((prev: any) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(prev)
        : updaterOrValue
    );
  };

  const table = useReactTable({
    columns,
    data,
    manualPagination: true,
    manualFiltering: true,
    manualSorting,
    pageCount: Math.ceil(totalRecords / pageSize),
    state: {
      sorting,
      pagination: {
        pageIndex,
        pageSize,
      },
      columnFilters,
    },
    onSortingChange,
    onColumnFiltersChange: handleColumnFilterChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: !manualSorting ? getSortedRowModel() : undefined,
  });

  const { getHeaderGroups, getRowModel, getCanPreviousPage, getCanNextPage } =
    table;

  return (
    <Fragment>
      {/* Sticky column styles */}
      <style>
        {`
        .sticky-column {
          position: sticky;
          right: 0;
          z-index: 10;
          background-color: #fff !important;
          border-left: 1px solid #dee2e6;
        }

        thead .sticky-column {
          background-color: #EEF0F7 !important;
        }

        .sticky-action {
          position: sticky;
          right: 0;
          z-index: 10;
          background-color: #fff;
          border-left: 1px solid #dee2e6;
          text-align: center; 
          vertical-align: middle;
        }

        .table td,
        .table th {
          background-clip: padding-box;
        }
      `}
      </style>

      <div className="table-responsive table-card">
        <Table
          className={`table table-centered align-middle table-custom-effect table-nowrap mb-0 ${
            isBordered ? "table-bordered" : ""
          }`}
          bordered={isBordered}
        >
          <thead className="table-light">
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                    const isLastColumn = index === headerGroup.headers.length - 1;
                    const isSorted = header.column.getIsSorted();
                    const canSort = header.column.getCanSort();
                    const sortIcon =
                      isSorted === "asc" ? (
                        <ArrowUpwardIcon fontSize="small" />
                      ) : isSorted === "desc" ? (
                        <ArrowDownwardIcon fontSize="small" />
                      ) : (
                        <UnfoldMoreIcon fontSize="small" />
                      );

                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        className={`align-middle${isLastColumn ? " sticky-column sticky-action text-center" : ""}`}
                        style={isLastColumn ? { verticalAlign: "middle" } : {}}
                      >
                        {!header.isPlaceholder && (
                          <div
                            className="d-flex justify-content-between align-items-center"
                            style={{ gap: 4 }}
                          >
                            <div
                              className="d-flex align-items-center"
                              style={{
                                gap: 4,
                                cursor: canSort ? "pointer" : "default",
                              }}
                              onClick={
                                canSort
                                  ? (e) => {
                                      const currentSort = header.column.getIsSorted();
                                      if (!currentSort) {
                                        // No sort -> ascending
                                        header.column.toggleSorting(false);
                                      } else if (currentSort === "asc") {
                                        // Ascending -> descending
                                        header.column.toggleSorting(true);
                                      } else {
                                        // Descending -> clear sort
                                        header.column.clearSorting();
                                      }
                                    }
                                  : undefined
                              }
                            >
                              <span>
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </span>
                              {canSort && sortIcon}
                            </div>

                            {header.column.getCanFilter() ? (
                              (header.column.columnDef as CustomColumnDef<any>)
                                .filterType === "date" ? (
                                <DateRangeFilterPopover
                                  column={header.column}
                                  setColValue={colValue}
                                  iconColor={
                                    header.column.getFilterValue()
                                      ? "#3258d3"
                                      : "inherit"
                                  }
                                />
                              ) : (
                                <FilterPopover
                                  column={header.column}
                                  columnDef={header.column.columnDef}
                                  columnFilters={columnFilters}
                                  setColumnFilters={setColumnFilters}
                                  setColValue={colValue}
                                  iconColor={
                                    header.column.getFilterValue()
                                      ? "#3258d3"
                                      : "inherit"
                                  }
                                  scrollableFilterPopover={scrollableFilterPopover}
                                />
                              )
                            ) : null}
                          </div>
                        )}
                      </th>
                    );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  No Data Found
                </td>
              </tr>
            ) : (
              getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell, index) => {
                      const isLastColumn = index === row.getVisibleCells().length - 1;
                      return (
                        <td
                          key={cell.id}
                          className={isLastColumn ? "sticky-column sticky-action text-center" : ""}
                          style={isLastColumn ? { verticalAlign: "middle" } : {}}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {isPagination && (
        <Row className="align-items-center mt-4 pt-3 gy-2">
          <Pagination
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </Row>
      )}
    </Fragment>
  );
};

export default TableContainer;
